<template>
  <div
    v-if="shouldShowBanner"
    :class="[$style.banner, isWarn && $style.expiredBanner]"
  >
    <div :class="$style.content">
      <span :class="$style.icon">{{ isWarn ? '⚠️' : '🎉' }}</span>
      <span
        :class="[$style.text, isWarn && $style.expiredText, 'type-body-medium']"
      >
        <template v-if="presentation === 'provisioning'">
          <strong>Activating your trial</strong> — this only takes a moment.
        </template>
        <template v-else-if="presentation === 'trialExpired'">
          <strong>Trial Expired</strong> — Upgrade to unlock all features.
        </template>
        <!-- Payment failed. During the grace window the customer can still fix it
             and lose nothing; afterwards paid features pause but the account,
             users and data survive — so neither message may sound like deletion. -->
        <template v-else-if="presentation === 'pastDue' && graceExpired">
          <strong>Paid features are paused</strong> — Update your payment method
          to restore your Yogen subscription. Your account, users, and data have
          been preserved.
        </template>
        <template v-else-if="presentation === 'pastDue'">
          <strong>Payment issue</strong> — We couldn't process your subscription
          payment. Update your payment method{{
            graceEndsAtLabel ? ` by ${graceEndsAtLabel}` : ''
          }}
          to avoid losing access to paid features.
        </template>
        <template v-else>
          <strong>Trial Active</strong> — {{ daysRemaining }} days remaining.
        </template>
      </span>
      <NuxtLink
        v-if="presentation !== 'provisioning'"
        to="/organizationSettings?tab=administration"
        :class="[
          $style.link,
          isWarn && $style.expiredLink,
          'type-label-large-emphasized',
        ]"
      >
        {{ linkLabel }}
      </NuxtLink>
    </div>
    <button
      v-if="presentation === 'trialing'"
      :class="$style.dismiss"
      @click="dismissBanner"
      aria-label="Dismiss banner"
    >
      ×
    </button>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, onMounted, watch } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useOrgStore } from '~/stores/orgStore'
  import { useUserStore } from '~/stores/userStore'
  import {
    getSubscriptionPresentation,
    trialDaysRemaining,
  } from '~/utils/subscriptionState'

  const BANNER_DISMISSED_KEY_PREFIX = 'yogen_trial_banner_dismissed_'

  const orgStore = useOrgStore()
  const userStore = useUserStore()
  const { selected: org } = storeToRefs(orgStore)
  const { user } = storeToRefs(userStore)

  const isDismissed = ref(false)

  const getBannerDismissedKey = () => {
    return `${BANNER_DISMISSED_KEY_PREFIX}${user.value?.id || 'unknown'}`
  }

  const checkDismissedState = () => {
    if (import.meta.client && user.value?.id) {
      const dismissed = localStorage.getItem(getBannerDismissedKey())
      isDismissed.value = dismissed === 'true'
    }
  }

  onMounted(() => {
    checkDismissedState()
  })

  watch(
    () => user.value?.id,
    () => {
      checkDismissedState()
    },
  )

  /**
   * The single derived subscription-presentation state (shared with LeftNav and
   * the Administration tab). Critically, a plain `free` org is NOT treated as an
   * expired trial.
   */
  const presentation = computed(() => getSubscriptionPresentation(org.value))

  // Warn styling for the states that need attention (expired / activating /
  // failed payment).
  const isWarn = computed(
    () =>
      presentation.value === 'trialExpired' ||
      presentation.value === 'provisioning' ||
      presentation.value === 'pastDue',
  )

  /**
   * Backend-computed payment-grace details, attached to GET /org as org.access
   * (middleware.ComputeOrgAccess). Never re-derive the grace window here — the
   * backend owns that rule and the Administration tab reads the same fields.
   */
  interface OrgAccess {
    platform?: boolean
    premium?: boolean
    pastDueAt?: string
    graceEndsAt?: string
    inGracePeriod?: boolean
    graceDaysRemaining?: number
  }

  const orgAccess = computed<OrgAccess | null>(
    () => (org.value as { access?: OrgAccess } | null)?.access ?? null,
  )

  const graceEndsAtLabel = computed(() => {
    const endsAt = orgAccess.value?.graceEndsAt
    if (!endsAt) return null
    const d = new Date(endsAt)
    if (Number.isNaN(d.getTime())) return null
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  })

  /**
   * True only when the backend explicitly says the grace window has closed.
   * `inGracePeriod` is omitted from the payload when false, so "grace details
   * present AND not in grace" is the expired signal; missing details fail
   * toward the softer in-grace message.
   */
  const graceExpired = computed(() => {
    const a = orgAccess.value
    if (!a || !a.graceEndsAt) return false
    return !a.inGracePeriod
  })

  const linkLabel = computed(() => {
    if (presentation.value === 'trialExpired') return 'Upgrade Now'
    if (presentation.value === 'pastDue') return 'Update Payment Method'
    return 'Manage Subscription'
  })

  /**
   * Days remaining in the trial period.
   */
  const daysRemaining = computed(() =>
    trialDaysRemaining(org.value?.trialEndsAt),
  )

  /**
   * Determine if the banner should be displayed.
   * Shown to all org members so everyone sees trial and billing status. A
   * genuinely free (never-trialed) or healthy paid org shows none. The
   * expired-trial and failed-payment banners are NOT dismissible so their CTA
   * stays visible while the account needs attention.
   */
  const shouldShowBanner = computed(() => {
    if (
      presentation.value === 'trialExpired' ||
      presentation.value === 'provisioning' ||
      // A failed payment is only fixable if the customer knows about it, and
      // the 7-day grace window is short — this one is never dismissible.
      presentation.value === 'pastDue'
    )
      return true
    if (presentation.value === 'trialing') return !isDismissed.value
    return false
  })

  /**
   * Permanently dismiss the banner for this user.
   */
  const dismissBanner = () => {
    isDismissed.value = true
    if (import.meta.client && user.value?.id) {
      localStorage.setItem(getBannerDismissedKey(), 'true')
    }
  }
</script>

<style module>
  .banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--color-surface-accent);
    border-bottom: 2px solid var(--color-interactive-link);
    padding: var(--space-8) var(--space-20);
    width: 100%;
    box-sizing: border-box;
    z-index: 100;
  }

  .expiredBanner {
    background: var(--color-status-warning-subtle);
    border-bottom-color: var(--color-status-warning);
  }

  .content {
    display: flex;
    align-items: center;
    gap: var(--space-12);
    flex-wrap: wrap;
  }

  .icon {
    font-size: 18px;
  }

  .text {
    color: var(--color-text-primary);
  }

  .expiredText {
    color: var(--color-text-primary);
  }

  .text strong {
    color: var(--color-interactive-link-hover);
  }

  .expiredText strong {
    color: var(--color-text-primary);
  }

  .link {
    color: var(--color-interactive-link);
    text-decoration: none;
    padding: var(--space-4) var(--space-12);
    background: var(--color-surface-default);
    border: 1px solid var(--color-interactive-link);
    border-radius: var(--radius-sm);
    transition: all 0.2s;
  }

  .link:hover {
    background: var(--color-interactive-link-hover);
    color: var(--color-text-on-brand);
    border-color: var(--color-interactive-link-hover);
  }

  .expiredLink {
    color: var(--color-text-primary);
    border-color: var(--color-status-warning);
    background: var(--color-status-warning-subtle);
  }

  .expiredLink:hover {
    background: var(--color-status-warning);
    color: var(--color-text-primary);
    border-color: var(--color-status-warning);
  }

  .dismiss {
    background: none;
    border: none;
    font-size: 20px;
    color: var(--color-icon-primary);
    cursor: pointer;
    padding: var(--space-4) var(--space-8);
    line-height: 1;
    opacity: 0.7;
    transition: opacity 0.2s;
  }

  .dismiss:hover {
    opacity: 1;
  }
</style>
